import { z } from 'zod';

import { AREA_NAME_MAX, AreaIdSchema } from './area.schema';
import { SecuritySchema } from './security.schema';

/**
 * Wire ceiling for each shortlist on `GET /v1/securities/:ticker/peers`.
 * Matches `PEER_PANEL_SIZE` in core (plan §6.6 · ADR-0031).
 */
export const PEERS_MAX = 9;

function refineIndustryIdentity(
  val: {
    readonly industry: readonly unknown[];
    readonly industryId: string | null;
    readonly industryName: string | null;
  },
  ctx: z.RefinementCtx,
): void {
  const idNull = val.industryId === null;
  const nameNull = val.industryName === null;
  if (idNull !== nameNull) {
    ctx.addIssue({
      code: 'custom',
      path: [idNull ? 'industryId' : 'industryName'],
      message: 'industryId and industryName are both null or both present',
    });
  }
  if (val.industry.length > 0 && idNull) {
    ctx.addIssue({
      code: 'custom',
      path: ['industry'],
      message: 'industry shortlist requires a classified industryId',
    });
  }
}

/**
 * `GET /v1/securities/:ticker/peers` (plan §6.6) — GICS leaders and industry
 * leaders in one payload because the stock screen renders both from one window
 * and both share the universe snapshot already paid for by F14.
 *
 * `industryId` and `industryName` are coupled: both null (unpublished
 * membership) or both present (a navigable `/area/$industryId`). An empty
 * `industry` list with a present id is classified-but-empty, not a catch-all
 * of other unclassified names (ADR-0026).
 */
export const PeersPayloadSchema = z
  .object({
    sector: z.array(SecuritySchema).max(PEERS_MAX),
    industry: z.array(SecuritySchema).max(PEERS_MAX),
    industryId: AreaIdSchema.nullable(),
    industryName: z.string().min(1).max(AREA_NAME_MAX).nullable(),
  })
  .superRefine(refineIndustryIdentity);
export type PeersPayload = z.infer<typeof PeersPayloadSchema>;
